/**
 * V2McpAuthorizePage (PRP-MCP-1), route `/mcp/authorize?request=<id>`.
 *
 * The OAuth consent screen a chat app (Claude, ChatGPT) opens in the browser.
 * It sits inside `ProtectedRoute`, so a signed-out visitor goes through the
 * normal login and comes back here (`lib/postLoginReturn.ts`). Allow and Deny
 * each post the decision and then send the browser to the `redirect_url` the
 * backend returns. An expired request (404) shows the backend's detail text.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { McpAPI, isSafeRedirect } from '../../lib/mcpApi';
import type { McpOAuthDecision, McpOAuthRequest } from '../../lib/mcpApi';
import { clearReturnPath } from '../../lib/postLoginReturn';
import type { ApiError } from '../../types';
import { Button } from '../components/ui/button';
import { Banner } from '../components/ui/banner';
import { Card } from '../components/ui/card';

/** OAuth consent page. */
export default function V2McpAuthorizePage() {
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get('request');
  const [req, setReq] = useState<McpOAuthRequest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // The sign-in detour (if any) is over once this page is showing.
    clearReturnPath();
    if (!requestId) {
      setError('This link is missing its request. Start the connection again from your chat app.');
      return;
    }
    let cancelled = false;
    McpAPI.getOAuthRequest(requestId)
      .then((r) => {
        if (!cancelled) setReq(r);
      })
      .catch((err: ApiError) => {
        if (!cancelled) setError(err.message || 'Could not load this request.');
      });
    return () => {
      cancelled = true;
    };
  }, [requestId]);

  const decide = async (fn: (_id: string) => Promise<McpOAuthDecision>) => {
    if (!requestId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fn(requestId);
      if (!isSafeRedirect(res.redirect_url)) {
        setError('This app asked to send you to an address AIEA will not open. Nothing was shared.');
        setBusy(false);
        return;
      }
      window.location.assign(res.redirect_url);
    } catch (err) {
      setError((err as ApiError).message || 'That did not go through. Try again.');
      setBusy(false);
    }
  };

  return (
    <div className="max-w-[520px] mx-auto pt-6">
      <Card className="p-[22px] space-y-4">
        <h1 className="m-0 text-[17px] font-semibold text-[var(--tx-hi)]">Connect a chat app</h1>
        {error && <Banner tone="warn">{error}</Banner>}
        {!req && !error && <p className="m-0 text-[12.5px] text-[var(--tx-lo)]">Loading...</p>}
        {req && (
          <>
            <p className="m-0 text-[14px] text-[var(--tx-hi)]">
              <span className="font-semibold">{req.client_name}</span> wants to work on your AIEA
              estimates.
            </p>
            {req.known_app ? (
              <p className="m-0 text-[12.5px] text-[var(--tx-lo)]">
                It will send you back to {req.known_app} at{' '}
                <span className="font-[var(--font-mono)]">{req.redirect_host}</span>.
              </p>
            ) : (
              <Banner tone="warn">
                AIEA does not recognize this app. The name above is what the app calls itself.
                After you allow it, your browser goes to{' '}
                <span className="font-[var(--font-mono)] break-all">{req.redirect_uri}</span>. Only
                allow it if you started this connection and trust that address.
              </Banner>
            )}
            <p className="m-0 text-[13px] text-[var(--tx-mid)]">
              Its edits apply to your estimates at once. Each one shows on the estimate page tagged
              &quot;from chat,&quot; and you can undo any of them there or in the chat. Exporting an
              ESX file still happens only in this web app.
            </p>
            <div className="flex gap-2 justify-end">
              <Button disabled={busy} onClick={() => void decide(McpAPI.denyOAuthRequest)}>
                Deny
              </Button>
              <Button
                variant="primary"
                disabled={busy}
                onClick={() => void decide(McpAPI.approveOAuthRequest)}
              >
                Allow
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
